import React from 'react';
import gql from 'graphql-tag';
import { ApolloProvider, Query, Mutation } from 'react-apollo';
import client from '../lib/apolloClient';
import OwnerDiagnosticQuestion from '../components/OwnerDiagnostic/OwnerDiagnosticQuestion';
import ProgressMeter from '../components/OwnerDiagnostic/ProgressMeter';
import OwnerDiagnosticResults from '../components/OwnerDiagnostic/OwnerDiagnosticResults';

const GET_CURRENT_USER = gql`
  query GetCurrentUserForDiagnostic {
    getCurrentUser {
      id
      diag_owner_answers
      diag_business_answers
      diag_readiness_total
      diag_attractiveness_total
      diag_quadrant
      diag_completed_at
    }
  }
`;

const UPSERT_OWNER_DIAGNOSTIC = gql`
  mutation UpsertOwnerDiagnostic($q_index: Int, $value: Int, $complete: Boolean) {
    upsertOwnerDiagnostic(q_index: $q_index, value: $value, complete: $complete) {
      user {
        id
        diag_owner_answers
        diag_business_answers
        diag_readiness_total
        diag_attractiveness_total
        diag_quadrant
        diag_completed_at
      }
      errors
    }
  }
`;

const SEND_ANALYTICS_EVENT = gql`
  mutation SendAnalyticsEvent($eventName: String!) {
    sendAnalyticsEvent(eventName: $eventName)
  }
`;

class OwnerDiagnosticContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { step: 0, showResults: false };
  }

  render() {
    return (
      <ApolloProvider client={client}>
        <Query query={GET_CURRENT_USER} fetchPolicy="cache-and-network" errorPolicy="all">
          {({ data, loading, error }) => {
            if (loading) return <div>Loading...</div>;
            // Allow rendering even when GraphQL returns partial data with errors
            if (!data || !data.getCurrentUser) return <div>Error loading data</div>;
            const user = (data && data.getCurrentUser) || {};
            const ownerAnswers = Array.isArray(user.diag_owner_answers) ? user.diag_owner_answers : Array(6).fill(null);
            const businessAnswers = Array.isArray(user.diag_business_answers) ? user.diag_business_answers : Array(6).fill(null);
            const answers = ownerAnswers.concat(businessAnswers);

            const isComplete = answers.filter(a => a !== null && a !== undefined).length === 12;
            const effectiveStep = Math.min(this.state.step, 11);

            return (
              <Mutation mutation={SEND_ANALYTICS_EVENT}>
                {(sendEvent) => (
                  <Mutation mutation={UPSERT_OWNER_DIAGNOSTIC}>
                    {(upsert, { loading: saving }) => {
                      const handleSelect = async (value) => {
                        const q_index = Math.min(this.state.step, 11);
                        await upsert({ variables: { q_index, value } });
                        try { await sendEvent({ variables: { eventName: 'diag_question_answered' } }); } catch(e) {}
                        const nextStep = Math.min(this.state.step + 1, 12);
                        if (q_index === 11) {
                          try { await upsert({ variables: { complete: true } }); } catch(e) {}
                          try { await sendEvent({ variables: { eventName: 'diag_completed' } }); } catch(e) {}
                          this.setState({ step: nextStep, showResults: true });
                        } else {
                          this.setState({ step: nextStep });
                        }
                      };

                      const handleBack = () => {
                        this.setState({ step: Math.max(effectiveStep - 1, 0) });
                      };

                      const handleComplete = async () => {
                        await upsert({ variables: { complete: true } });
                        try { await sendEvent({ variables: { event_name: 'diag_completed' } }); } catch(e) {}
                        this.setState({ showResults: true });
                      };

                      if (this.state.showResults || (isComplete && effectiveStep >= 12)) {
                        return (
                          <OwnerDiagnosticResults
                            readinessTotal={user.diag_readiness_total}
                            attractivenessTotal={user.diag_attractiveness_total}
                            quadrant={user.diag_quadrant}
                            saving={saving}
                          />
                        );
                      }

                      const value = answers[effectiveStep];
                      const sectionTitle = effectiveStep < 6 ? 'Owner Readiness' : 'Business Attractiveness';
                      const indexWithinSection = effectiveStep < 6 ? effectiveStep + 1 : effectiveStep - 5;

                      return (
                        <div>
                          <div className="px-4 md:px-0 max-w-3xl mx-auto mt-2">
                            <ProgressMeter current={effectiveStep} total={12} />
                          </div>
                          <OwnerDiagnosticQuestion
                            index={effectiveStep}
                            sectionTitle={sectionTitle}
                            indexWithinSection={indexWithinSection}
                            value={value}
                            onSelect={handleSelect}
                            onBack={handleBack}
                            saving={saving}
                          />
                        </div>
                      );
                    }}
                  </Mutation>
                )}
              </Mutation>
            );
          }}
        </Query>
      </ApolloProvider>
    );
  }
}

export default OwnerDiagnosticContainer;


